/**
 * Pure helpers for the listing Marketing tab (listing-marketing-hub). No I/O.
 */
import { LISTING_SITE_LABELS, type ExternalListingPostDto } from './external-listing-posts.api';

/** BR-2: a listing is Marketed once it has at least one posted external post record. */
export function isMarketed(postedCount: number): boolean {
  return postedCount >= 1;
}

/** The records that are still live on their site (status `posted`), in the order given. */
export function postedPosts(posts: ExternalListingPostDto[] | undefined): ExternalListingPostDto[] {
  return (posts ?? []).filter((p) => p.status === 'posted');
}

/** "Craigslist", "Facebook Marketplace and Craigslist": the distinct sites of `posts`, in order. */
export function siteNames(posts: ExternalListingPostDto[]): string {
  const names = [...new Set(posts.map((p) => LISTING_SITE_LABELS[p.site]))];
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * "1 post live" / "3 posts live". Counts posted records, not distinct sites: two Craigslist posts are
 * two posts, and the listings index only carries the record count.
 */
export function postedLabel(count: number): string {
  return `${count} ${count === 1 ? 'post' : 'posts'} live`;
}

export type MessengerReplyVariant = 'page' | 'email';

interface MessengerReplyInput {
  publicUrl: string | null;
  intakeEmail: string | null;
  /** How the renter knows the place, e.g. "412 Alder St, Unit 3". */
  address: string;
}

/**
 * The canned reply a landlord pastes into each Messenger inquiry (BR-8): one variant points to the
 * public listing page, the other to the listing's Resida email. Deterministic for its inputs.
 */
export function buildMessengerReply(variant: MessengerReplyVariant, input: MessengerReplyInput): string {
  const place = input.address ? `the place at ${input.address}` : 'the place';
  if (variant === 'page') {
    const where = input.publicUrl ? `here: ${input.publicUrl}` : 'on its Resida listing page';
    return `Hi! Thanks for your interest in ${place}. You can see photos, the rent and move-in costs, and request a showing ${where}. I respond fastest there.`;
  }
  const where = input.intakeEmail ? `email me at ${input.intakeEmail}` : 'email me';
  return `Hi! Thanks for your interest in ${place}. For photos, the rent and move-in costs, or to set up a showing, ${where}. I respond fastest there.`;
}
